import React, { useEffect } from "react";
import enFallback from '../language-en.json';
import arFallback from '../language-ar.json';
import CircularLoading from "../components/CircularLoading";
import { useSelector, useDispatch } from "react-redux";
import { api } from "common";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import moment from "moment/min/moment-with-locales";

function AuthLoading(props) {
  const { t } = useTranslation();
  const {
    fetchUser,
    fetchCarTypes,
    fetchSettings,
    fetchBookings,
    fetchCancelReasons,
    fetchPromos,
    fetchDriverEarnings,
    fetchUsers,
    fetchNotifications,
    fetchEarningsReport,
    signOff,
    fetchWithdraws,
    fetchPaymentMethods,
    fetchLanguages,
    fetchWalletHistory,
    fetchCars,
    fetchComplain,
    fetchSMTP,
    fetchSos,
    fetchSMSConfig,
    fetchTasks
  } = api;
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const languagedata = useSelector((state) => state.languagedata);
  const settingsdata = useSelector((state) => state.settingsdata);

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch, fetchSettings]);

  useEffect(() => {
    let obj = {};
    let def1 = {};
    if (languagedata.langlist) {
      for (const value of Object.values(languagedata.langlist)) {
        const fallback = value.langLocale === 'ar' ? arFallback : enFallback;
        obj[value.langLocale] = { ...fallback, ...value.keyValuePairs };
        if (value.default === true) {
          def1 = value;
        }
      }
      if (!obj['en']) obj['en'] = enFallback;
      if (!obj['ar']) obj['ar'] = arFallback;

      const result = localStorage.getItem('lang');
      if (result) {
        try {
          let parsed = JSON.parse(result);
          let langLocale = parsed['langLocale'] || 'en';
          let dateLocale = parsed['dateLocale'] || 'en';
          let bundle = obj[langLocale] || (langLocale === 'ar' ? arFallback : enFallback);

          i18n.addResourceBundle(
            langLocale,
            "translations",
            bundle,
            true,
            true
          );
          i18n.changeLanguage(langLocale);
          document.documentElement.dir = langLocale === 'ar' ? 'rtl' : 'ltr';
          moment.locale(dateLocale);
        } catch (e) {
          console.error("Language parsing error:", e);
        }
      } else if (def1 && def1.langLocale) {
        let bundle = obj[def1.langLocale] || enFallback;
        i18n.addResourceBundle(
          def1.langLocale,
          "translations",
          bundle,
          true,
          true
        );
        i18n.changeLanguage(def1.langLocale);
        document.documentElement.dir = def1.langLocale === 'ar' ? 'rtl' : 'ltr';
        moment.locale(def1.dateLocale);
      }

      dispatch(fetchUser());
    } else if (languagedata.error.flag || !languagedata.loading) {
      // Fallback if DB is empty or fails
      const result = localStorage.getItem('lang');
      let currentLang = 'en';
      if (result) {
        try {
          currentLang = JSON.parse(result)['langLocale'] || 'en';
        } catch (e) {}
      }
      const bundle = currentLang === 'ar' ? arFallback : enFallback;
      i18n.addResourceBundle(currentLang, "translations", bundle, true, true);
      i18n.changeLanguage(currentLang);
      document.documentElement.dir = currentLang === 'ar' ? 'rtl' : 'ltr';
      moment.locale(currentLang);
      dispatch(fetchUser());
    }
  }, [languagedata, dispatch, fetchUser]);

  useEffect(() => {
    if (settingsdata.settings) {
      dispatch(fetchLanguages());
      dispatch(fetchCarTypes());
      document.title = (settingsdata.settings.appName && settingsdata.settings.appName !== "MOVE" && settingsdata.settings.appName !== "Move") ? settingsdata.settings.appName : "Rahl";
    }
  }, [settingsdata.settings, dispatch, fetchLanguages, fetchCarTypes]);

  useEffect(() => {
    if (auth.profile) {
      if (auth.profile.usertype) {
        let role = auth.profile.usertype;
        if (role === "customer") {
          dispatch(fetchBookings());
          dispatch(fetchWalletHistory());
          dispatch(fetchPaymentMethods());
          dispatch(fetchCancelReasons());
          dispatch(fetchUsers());
        } else if (role === "driver") {
          dispatch(fetchBookings());
          dispatch(fetchWithdraws());
          dispatch(fetchPaymentMethods());
          dispatch(fetchCars());
          dispatch(fetchWalletHistory());
          dispatch(fetchTasks());
        } else if (role === "admin") {
          dispatch(fetchUsers());
          dispatch(fetchBookings());
          dispatch(fetchPromos());
          dispatch(fetchDriverEarnings());
          dispatch(fetchNotifications());
          dispatch(fetchEarningsReport());
          dispatch(fetchCancelReasons());
          dispatch(fetchWithdraws());
          dispatch(fetchComplain());
          dispatch(fetchPaymentMethods());
          dispatch(fetchCars());
          dispatch(fetchSMTP());
          dispatch(fetchSMSConfig());
          dispatch(fetchSos());
        } else if (role === "fleetadmin") {
          dispatch(fetchUsers());
          dispatch(fetchBookings());
          dispatch(fetchDriverEarnings());
          dispatch(fetchCars());
          dispatch(fetchCancelReasons());
        } else {
          alert(t("not_valid_user_type"));
          dispatch(signOff());
        }
      } else {
        alert(t("user_issue_contact_admin"));
        dispatch(signOff());
      }
    }
  }, [
    auth.profile,
    dispatch,
    fetchBookings,
    fetchCancelReasons,
    fetchDriverEarnings,
    fetchEarningsReport,
    fetchNotifications,
    fetchPromos,
    fetchUsers,
    fetchWithdraws,
    signOff,
    fetchPaymentMethods,
    fetchWalletHistory,
    fetchCars,
    fetchComplain,
    fetchSMTP,
    fetchSMSConfig,
    fetchSos,
    fetchTasks,
    t
  ]);

  return settingsdata.loading ? (
    <CircularLoading />
  ) : settingsdata.settings ? (
    auth.loading || (!languagedata.langlist && !languagedata.error.flag) ? (
      <CircularLoading />
    ) : (
      props.children
    )
  ) : (
    <div>
      <span>No Database Settings found</span>
    </div>
  );
}

export default AuthLoading;
